import type React from "react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Separator } from "@/shared/components/ui/separator"
import { cn } from "@/shared/lib/utils"

interface AdminSectionCardProps {
  title?: string
  description?: string
  children: React.ReactNode
  footer?: React.ReactNode
  className?: string
  contentClassName?: string
  headerClassName?: string
  noPadding?: boolean
  variant?: "default" | "elevated" | "outlined"
}

/**
 * AdminSectionCard - A consistent card component for admin content sections
 */
export const AdminSectionCard: React.FC<AdminSectionCardProps> = ({
  title,
  description,
  children,
  footer,
  className,
  contentClassName,
  headerClassName,
  noPadding = false,
  variant = "default"
}) => {
  const variantStyles = {
    default: "transition-shadow duration-300 hover:shadow-md",
    elevated: "shadow-md hover:shadow-lg transition-shadow duration-300",
    outlined: "border-2 transition-shadow duration-300 hover:shadow-md"
  }
  
  return (
    <Card className={cn("rounded-lg", variantStyles[variant], className)}>
      {(title || description) && (
        <CardHeader className={cn("pb-3", headerClassName)}>
          {title && (
            <CardTitle className="text-lg font-semibold">
              {title}
            </CardTitle>
          )}
          {description && (
            <CardDescription className="text-sm">
              {description}
            </CardDescription>
          )}
        </CardHeader>
      )}
      
      <CardContent className={cn(
        noPadding ? "p-0" : "pt-0",
        contentClassName
      )}>
        {children}
      </CardContent>

      {footer && (
        <>
          <Separator />
          <CardFooter className="pt-4 pb-4">
            {footer}
          </CardFooter>
        </>
      )}
    </Card>
  )
}

interface AdminStatCardProps {
  title: string
  value: string | number
  description?: string
  icon?: React.ReactNode
  trend?: {
    value: number
    label: string
    positive?: boolean
  }
  className?: string
}

/**
 * AdminStatCard - Dashboard statistics card with optional trend indicator
 */
export const AdminStatCard: React.FC<AdminStatCardProps> = ({
  title,
  value,
  description,
  icon,
  trend,
  className
}) => {
  return (
    <Card className={cn("rounded-lg", className)}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              {title}
            </p>
            <div className="flex items-baseline gap-2">
              <h3 className="text-3xl font-bold tracking-tight">
                {value}
              </h3>
              {trend && (
                <span className={cn(
                  "text-sm font-medium flex items-center gap-1",
                  trend.positive ? "text-green-600" : "text-red-600"
                )}>
                  <span className={cn(
                    "inline-block",
                    trend.positive ? "rotate-0" : "rotate-180"
                  )}>↑</span>
                  {trend.value}%
                </span>
              )}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
            {trend && (
              <p className="text-xs text-muted-foreground">{trend.label}</p>
            )}
          </div>
          {icon && (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
              {icon}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface AdminEmptyStateProps {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
  className?: string
}

/**
 * AdminEmptyState - Consistent empty state for lists and tables
 */
export const AdminEmptyState: React.FC<AdminEmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  className
}) => {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center py-16 px-4 text-center",
      className
    )}>
      {icon && (
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted text-muted-foreground mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold mb-1">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          {description}
        </p>
      )}
      {action && (
        <div className="mt-2">
          {action}
        </div>
      )}
    </div>
  )
}
